import { useState, useEffect } from 'react'
import { botsApi, type Bot } from '../../api/bots'
import { useUiStore } from '../../stores/uiStore'
import { Select } from '../ui/Select'

interface Props {
  onBack: () => void
}

const CATEGORIES = ['utility', 'games', 'news', 'shopping', 'productivity', 'entertainment']

type View = 'list' | 'create' | { bot: Bot }

export function MyBotsPanel({ onBack }: Props) {
  const [bots, setBots] = useState<Bot[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<View>('list')
  const { addToast } = useUiStore()

  useEffect(() => {
    botsApi.listMine()
      .then((r) => setBots(r.bots ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить ботов'))
      .finally(() => setLoading(false))
  }, [addToast])

  if (view === 'create') {
    return (
      <CreateBotForm
        onBack={() => setView('list')}
        onCreate={(bot) => {
          setBots((prev) => [bot, ...prev])
          setView('list')
        }}
      />
    )
  }

  if (typeof view === 'object') {
    return (
      <BotDetailView
        bot={view.bot}
        onBack={() => setView('list')}
        onUpdate={(updated) => setBots((prev) => prev.map((b) => b.id === updated.id ? updated : b))}
        onDelete={(id) => { setBots((prev) => prev.filter((b) => b.id !== id)); setView('list') }}
      />
    )
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">🤖 My Bots</h2>
        <button
          onClick={() => setView('create')}
          className="text-accent text-sm font-medium hover:text-blue-400 transition-colors"
        >
          + New
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : bots.length === 0 ? (
          <div className="text-center py-10 px-4">
            <p className="text-4xl mb-2">🤖</p>
            <p className="text-txt-primary font-medium">No bots yet</p>
            <p className="text-txt-meta text-sm mt-1">Create your first bot to automate tasks</p>
            <button
              onClick={() => setView('create')}
              className="mt-4 bg-accent text-white px-5 py-2 rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors"
            >
              Create bot
            </button>
          </div>
        ) : (
          <div className="divide-y divide-divider">
            {bots.map((bot) => (
              <button
                key={bot.id}
                onClick={() => setView({ bot })}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-hover transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-bg-input flex items-center justify-center text-xl shrink-0">
                  🤖
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-txt-primary text-sm font-medium truncate">{bot.name}</p>
                  <p className="text-txt-meta text-xs">@{bot.username}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${bot.is_active ? 'bg-green-500/10 text-green-400' : 'bg-txt-meta/10 text-txt-meta'}`}>
                  {bot.is_active ? 'Active' : 'Inactive'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function CreateBotForm({ onBack, onCreate }: { onBack: () => void; onCreate: (bot: Bot) => void }) {
  const [form, setForm] = useState({ username: '', name: '', description: '', is_public: true, category: 'utility', web_app_url: '' })
  const [saving, setSaving] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const { addToast } = useUiStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.username || !form.name) { addToast('error', 'Требуется имя и username'); return }
    setSaving(true)
    try {
      const result = await botsApi.create({
        username: form.username,
        name: form.name,
        description: form.description || undefined,
        is_public: form.is_public,
        category: form.category,
        web_app_url: form.web_app_url || undefined,
      })
      setToken(result.token)
      onCreate(result)
    } catch {
      addToast('error', 'Не удалось создать бота')
    } finally {
      setSaving(false)
    }
  }

  if (token) {
    return (
      <div className="flex flex-col h-full bg-bg-primary">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
          <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <h2 className="text-txt-primary font-semibold text-base flex-1">Bot created!</h2>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 text-center">
            <p className="text-4xl mb-2">🎉</p>
            <p className="text-txt-primary font-semibold">Your bot is ready</p>
          </div>
          <div className="bg-bg-input rounded-2xl p-4">
            <p className="text-txt-meta text-xs mb-1">Bot Token (save this now!)</p>
            <p className="text-txt-primary text-xs font-mono break-all select-all">{token}</p>
          </div>
          <p className="text-txt-meta text-xs text-center">Keep this token secret. Use it to send requests to the bot API.</p>
          <button
            onClick={onBack}
            className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Create Bot</h2>
      </div>
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
        <div>
          <label className="text-txt-meta text-xs mb-1 block">Username *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-meta text-sm">@</span>
            <input
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') }))}
              placeholder="my_cool_bot"
              className="w-full bg-bg-input text-txt-primary rounded-xl pl-7 pr-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
            />
          </div>
        </div>
        <div>
          <label className="text-txt-meta text-xs mb-1 block">Name *</label>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Мой крутой бот"
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />
        </div>
        <div>
          <label className="text-txt-meta text-xs mb-1 block">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Что умеет этот бот?"
            rows={2}
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta resize-none"
          />
        </div>
        <div>
          <label className="text-txt-meta text-xs mb-1 block">Category</label>
          <Select
            value={form.category}
            onChange={(v) => setForm((f) => ({ ...f, category: v }))}
            className="w-full"
            options={CATEGORIES.map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))}
          />
        </div>
        <div>
          <label className="text-txt-meta text-xs mb-1 block">Web App URL (optional)</label>
          <input
            value={form.web_app_url}
            onChange={(e) => setForm((f) => ({ ...f, web_app_url: e.target.value }))}
            placeholder="https://example.com/app"
            type="url"
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="text-txt-secondary text-sm">Public in store</span>
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, is_public: !f.is_public }))}
            className={`w-11 h-6 rounded-full transition-colors relative ${form.is_public ? 'bg-accent' : 'bg-bg-input'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_public ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <button
          type="submit"
          disabled={saving || !form.username || !form.name}
          className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
        >
          {saving ? 'Создание…' : 'Создать бота'}
        </button>
      </form>
    </div>
  )
}

function BotDetailView({ bot, onBack, onUpdate, onDelete }: { bot: Bot; onBack: () => void; onUpdate: (b: Bot) => void; onDelete: (id: string) => void }) {
  const [token, setToken] = useState<string | null>(null)
  const [regen, setRegen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { addToast } = useUiStore()

  const handleRegenToken = async () => {
    setRegen(true)
    try {
      const r = await botsApi.regenerateToken(bot.id)
      setToken(r.token)
    } catch {
      addToast('error', 'Не удалось обновить токен')
    } finally {
      setRegen(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm(`Delete @${bot.username}? This cannot be undone.`)) return
    setDeleting(true)
    try {
      await botsApi.deleteBot(bot.id)
      addToast('success', 'Бот удалён')
      onDelete(bot.id)
    } catch {
      addToast('error', 'Не удалось удалить бота')
    } finally {
      setDeleting(false)
    }
  }

  void onUpdate // suppressed — update form could be a future enhancement

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1 truncate">{bot.name}</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-bg-input rounded-2xl p-4 space-y-2">
          <Row label="Username" value={`@${bot.username}`} />
          <Row label="Категория" value={bot.category} />
          <Row label="Status" value={bot.is_active ? '✅ Active' : '⭕ Inactive'} />
          <Row label="Installs" value={String(bot.install_count)} />
          {bot.rating > 0 && <Row label="Рейтинг" value={`${'⭐'.repeat(Math.round(bot.rating))} (${bot.rating.toFixed(1)})`} />}
          {bot.web_app_url && <Row label="Web App" value={bot.web_app_url} truncate />}
        </div>

        {bot.description && (
          <div className="bg-bg-input rounded-2xl p-4">
            <p className="text-txt-meta text-xs mb-1">Description</p>
            <p className="text-txt-secondary text-sm">{bot.description}</p>
          </div>
        )}

        {/* Token management */}
        <div className="bg-bg-input rounded-2xl p-4 space-y-2">
          <p className="text-txt-meta text-xs">Bot Token</p>
          {token ? (
            <p className="text-txt-primary text-xs font-mono break-all select-all">{token}</p>
          ) : (
            <p className="text-txt-meta text-xs">Token is hidden for security</p>
          )}
          <button
            onClick={handleRegenToken}
            disabled={regen}
            className="text-yellow-400 text-sm hover:text-yellow-300 transition-colors disabled:opacity-40"
          >
            {regen ? 'Обновление…' : '↺ Обновить токен'}
          </button>
        </div>

        <button
          onClick={handleDelete}
          disabled={deleting}
          className="w-full text-red-400 border border-red-400/20 hover:bg-red-400/10 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-40"
        >
          {deleting ? 'Удаление…' : '🗑 Удалить бота'}
        </button>
      </div>
    </div>
  )
}

function Row({ label, value, truncate }: { label: string; value: string; truncate?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-txt-meta text-xs shrink-0">{label}</span>
      <span className={`text-txt-primary text-xs text-right ${truncate ? 'truncate' : ''}`}>{value}</span>
    </div>
  )
}
